import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { TeamMemberState, TeamMemberPoints, TeamMember, TeamMemberSyncResponse } from './types'
import {
  fetchTeamMembers,
  fetchTeamMemberPoints,
  bulkSyncTeamMembers,
  fetchTeamMemberInfo,
} from './actions'

const initialState: TeamMemberState = {
  loadingTeamMembers: false,
  loadingTeamMemberPoints: false,
  loadingTeamMemberInfo: false,

  teamMemberInfo: null,

  teamMembers: [],
  teamMemberPoints: [],
  filters: {
    supervisor: [],
    operationalManager: [],
    jobProfile: [],
    practice: [],
  },
  searchQuery: '',

  syncMessage: undefined,
  syncCounts: undefined,

  errorTeamMembers: '',
  errorTeamMemberPoints: '',
  errorTeamMemberInfo: '',
}

export const teamMemberSlice = createSlice({
  name: 'teamMember',
  initialState,
  reducers: {
    setSupervisorFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.supervisor = action.payload
    },
    setOperationalManagerFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.operationalManager = action.payload
    },
    setJobProfileFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.jobProfile = action.payload
    },
    setPracticeFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.practice = action.payload
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },

    clearFilters: (state) => {
      state.filters = {
        supervisor: [],
        operationalManager: [],
        jobProfile: [],
        practice: [],
      }
    },

    clearSearchQuery: (state) => {
      state.searchQuery = ''
    },

    clearTeamMemberInfo: (state) => {
      state.teamMemberInfo = null
    },
  },
  extraReducers: (builder) => {
    // Fetch All Team Members
    builder
      .addCase(fetchTeamMembers.pending, (state) => {
        state.loadingTeamMembers = true
        state.errorTeamMembers = ''
      })
      .addCase(fetchTeamMembers.fulfilled, (state, action: PayloadAction<TeamMember[]>) => {
        state.loadingTeamMembers = false
        state.teamMembers = action.payload
        state.errorTeamMembers = ''
      })
      .addCase(fetchTeamMembers.rejected, (state, action) => {
        state.loadingTeamMembers = false
        state.errorTeamMembers = action.error.message || 'Failed to fetch team members'
      })

    // Fetch Team Member Points
    builder
      .addCase(fetchTeamMemberInfo.pending, (state) => {
        state.loadingTeamMemberInfo = true
        state.errorTeamMemberInfo = ''
      })
      .addCase(fetchTeamMemberInfo.fulfilled, (state, action: PayloadAction<TeamMember>) => {
        state.loadingTeamMemberInfo = false
        state.teamMemberInfo = action.payload
        state.errorTeamMemberInfo = ''
      })
      .addCase(fetchTeamMemberInfo.rejected, (state, action) => {
        state.loadingTeamMemberInfo = false
        state.errorTeamMemberInfo = action.error.message || 'Failed to fetch team member info'
      })

    // Fetch Team Member points
    builder
      .addCase(fetchTeamMemberPoints.pending, (state) => {
        state.loadingTeamMemberPoints = true
        state.errorTeamMemberPoints = ''
      })
      .addCase(fetchTeamMemberPoints.fulfilled, (state, action: PayloadAction<TeamMemberPoints[]>) => {
        state.loadingTeamMemberPoints = false
        state.teamMemberPoints = action.payload
        state.errorTeamMemberPoints = ''
      })
      .addCase(fetchTeamMemberPoints.rejected, (state, action) => {
        state.loadingTeamMemberPoints = false
        state.errorTeamMemberPoints = action.error.message || 'Failed to fetch team member points'
      })

    // Bulk Sync Team Members
    builder
      .addCase(bulkSyncTeamMembers.pending, (state) => {
        state.loadingTeamMembers = true
        state.errorTeamMembers = ''
      })
      .addCase(bulkSyncTeamMembers.fulfilled, (state, action: PayloadAction<TeamMemberSyncResponse>) => {
        state.loadingTeamMembers = false
        state.teamMembers = action.payload.teamMembers
        state.syncMessage = action.payload.message
        state.syncCounts = {
          updatedCount: action.payload.updatedCount,
          addedCount: action.payload.addedCount,
          terminatedCount: action.payload.terminatedCount,
        }
        state.filters = {
          supervisor: [],
          operationalManager: [],
          jobProfile: [],
          practice: [],
        }
        state.errorTeamMembers = ''
      })
      .addCase(bulkSyncTeamMembers.rejected, (state, action) => {
        state.loadingTeamMembers = false
        state.errorTeamMembers = action.error.message || 'Failed to bulk sync team members'
      })
  },
})

export const {
  setSupervisorFilter,
  setOperationalManagerFilter,
  setJobProfileFilter,
  setPracticeFilter,
  clearFilters,
  clearSearchQuery,
  setSearchQuery,
  clearTeamMemberInfo,
} = teamMemberSlice.actions

export default teamMemberSlice.reducer
